/**
 * Box 4: spend (bars) and clicks (line) per day up to 14 days, per ISO week
 * (keyed by its Monday) above (spec §3.3, ruling R14). Every bucket of the
 * range is present; a bucket with no rows is null — a gap, never 0. Partial
 * first and last weeks are kept, as Looker's date_week keeps them. Pure.
 *
 * @module services/overview/spendTrend
 */
import { addDays, rangeDays, type DateRange } from '@/services/looker/lookerDateRange';
import { addNullable } from './overviewMath';
import type { FactRow } from './overviewTypes';

export const TREND_DAILY_MAX_DAYS = 14;
export type TrendGrain = 'day' | 'week';
export interface TrendPoint { key: string; spend: number | null; clicks: number | null }
export interface Trend { grain: TrendGrain; points: TrendPoint[] }

/** The Monday of the ISO week holding `day` (UTC arithmetic on a plain date: no clock, no zone). */
export const isoWeekStart = (day: string): string => {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  return addDays(day, -((dow + 6) % 7));
};

export const trendOf = (rows: readonly FactRow[], range: DateRange): Trend => {
  const grain: TrendGrain = rangeDays(range) <= TREND_DAILY_MAX_DAYS ? 'day' : 'week';
  const keyOf = (d: string): string => (grain === 'day' ? d : isoWeekStart(d));
  const days = Array.from({ length: rangeDays(range) }, (_, i) => addDays(range.start, i));
  const keys = [...new Set(days.map(keyOf))];
  const points = keys.map((key): TrendPoint => {
    const own = rows.filter((r) => keyOf(r.date) === key);
    return {
      key,
      spend: own.reduce<number | null>((s, r) => addNullable(s, r.spend), null),
      clicks: own.reduce<number | null>((s, r) => addNullable(s, r.clicks), null),
    };
  });
  return { grain, points };
};
